import type { IRowNode } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  ButtonVariants,
  FormRowStatus,
  FormTable,
  FormattedMessage,
  GenerateCustomerApiKeyDialog,
  HStack,
  IconName,
  NotificationVariants,
  Portal,
  VStack,
  copyText,
  logger,
  promisesHaveResolved,
  useDisclosure,
  useFormTable,
  useGlobalToasts,
  useIntl,
  useWLUserContext,
  type Column,
  type CustomerUserApiKey,
} from '@talos/kyoko';
import { defineMessages } from 'react-intl';

const messages = defineMessages({
  generateApiKey: {
    defaultMessage: 'Generate API Key',
    id: 'Settings.generateApiKey',
  },
  save: {
    defaultMessage: 'Save',
    id: 'Settings.save',
  },
  apiKey: {
    defaultMessage: 'API Key',
    id: 'Settings.apiKey',
  },
  apiKeyLabel: {
    defaultMessage: 'Label',
    id: 'Settings.apiKeyLabel',
  },
  apiSecret: {
    defaultMessage: 'API Secret',
    id: 'Settings.apiSecret',
  },
  whitelistedIps: {
    defaultMessage: 'Whitelisted IPs',
    id: 'Settings.whitelistedIps',
  },
});

interface UserApiKeysFormTableProps {
  portalID: string;
}

function getApiSecret(data: CustomerUserApiKey) {
  return data.ApiSecret || '*****';
}

function useColumns() {
  const { add: addToast } = useGlobalToasts();
  const { formatMessage } = useIntl();

  return useMemo<Column[]>(
    () => [
      { type: 'date', field: 'Timestamp', width: 140, sort: '-' },
      {
        type: 'text',
        field: 'Label',
        title: formatMessage(messages.apiKeyLabel),
        editable: true,
        width: 110,
      },
      {
        type: 'text',
        field: 'ApiKey',
        title: formatMessage(messages.apiKey),
        width: 120,
      },
      {
        type: 'custom',
        field: 'ApiSecret',
        title: formatMessage(messages.apiSecret),
        width: 160,
        params: {
          valueGetter: ({ data }: { data: CustomerUserApiKey }) => getApiSecret(data),
        },
      },
      {
        type: 'text',
        field: 'WhitelistedIps',
        title: formatMessage(messages.whitelistedIps),
        editable: true,
        width: 130,
      },
      { type: 'filler', id: 'filler' },
      {
        type: 'iconButton',
        id: 'copy',
        pinned: 'right',
        params: {
          icon: IconName.ClipboardCopy,
          onClick: ({ node }: { node: IRowNode }) => {
            const rowData: CustomerUserApiKey = node.data;
            copyText(`USER:${rowData.CustomerUserID}
KEY:${rowData.ApiKey}
SECRET:${getApiSecret(rowData)}`);
            addToast({
              text: `Details copied to clipboard.`,
              variant: NotificationVariants.Default,
            });
          },
        },
        width: 40,
      },
      { type: 'remove', id: 'remove', pinned: 'right' },
    ],
    [addToast, formatMessage]
  );
}

export function UserApiKeysFormTable({ portalID }: UserApiKeysFormTableProps) {
  const { createUserApiKey, deleteUserApiKey, getUserApiKeys, updateUserApiKey } = useWLUserContext();
  const { add: addToast } = useGlobalToasts();
  const { formatMessage } = useIntl();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [userApiKeys, setUserApiKeys] = useState<CustomerUserApiKey[] | undefined>();
  const columns = useColumns();
  const generateDialog = useDisclosure();

  const formTable = useFormTable<CustomerUserApiKey>({
    rowID: 'CustomerUserApiKeyID',
    data: userApiKeys,
    columns,
  });

  const handleSave = useCallback(() => {
    setIsSaving(true);
    const rows = formTable.getRows();
    const requests: Promise<any>[] = [];
    for (const row of rows) {
      if (row.status === FormRowStatus.Updated) {
        requests.push(
          updateUserApiKey(row.data)
            .then(({ data }) => {
              row.setData(data[0]);
              return data[0];
            })
            .catch(e => {
              logger.error(e as Error);
              addToast({
                text: e?.toString() || `Could not update api key.`,
                variant: NotificationVariants.Negative,
              });
            })
        );
      }
      if (row.status === FormRowStatus.Removed) {
        requests.push(
          deleteUserApiKey(row.data.CustomerUserApiKeyID)
            .then(() => {
              row.remove(true);
              return row.data;
            })
            .catch(e => {
              logger.error(e as Error);
              addToast({
                text: e?.toString() || `Could not delete api key.`,
                variant: NotificationVariants.Negative,
              });
            })
        );
      }
    }
    Promise.allSettled(requests).then(promises => {
      setIsSaving(false);
      if (promisesHaveResolved(promises)) {
        addToast({
          text: 'Customer User API Keys saved.',
          variant: NotificationVariants.Positive,
        });
      } else {
        addToast({
          text: 'Customer User API Keys could not be saved.',
          variant: NotificationVariants.Negative,
        });
      }
    });
  }, [addToast, deleteUserApiKey, updateUserApiKey, formTable]);

  useEffect(() => {
    getUserApiKeys().then(({ data }) => setUserApiKeys(data));
  }, [getUserApiKeys, setUserApiKeys]);

  return (
    <VStack alignItems="stretch" h="100%" w="100%">
      <Portal portalId={portalID}>
        <HStack gap="spacing8">
          <Button startIcon={IconName.Plus} variant={ButtonVariants.Positive} onClick={() => generateDialog.open()}>
            <FormattedMessage {...messages.generateApiKey} />
          </Button>
          <Button variant={ButtonVariants.Primary} onClick={handleSave} disabled={isSaving || !formTable.isDirty}>
            <FormattedMessage {...messages.save} />
          </Button>
        </HStack>
      </Portal>
      <GenerateCustomerApiKeyDialog
        {...generateDialog}
        title={formatMessage(messages.generateApiKey)}
        label={formatMessage(messages.apiKeyLabel)}
        confirmLabel={formatMessage(messages.generateApiKey)}
        onGenerate={label =>
          createUserApiKey(label).then(({ data }) => {
            // Doesn't need to be saved, already confirmed to be generated
            formTable.addRow(data[0], FormRowStatus.None);
          })
        }
      />
      <FormTable {...formTable} />
    </VStack>
  );
}
